/**
 * Renders the per-block editor. Each block has a `type` that maps to a field schema.
 * This data-driven approach lets us add new block types in one place — no UI rewrites.
 */
import { useState } from "react";
import { Plus, Trash2, ChevronDown, ChevronUp, ExternalLink, Wand2, X as XIcon } from "lucide-react";
import { api } from "./api";
import ImageSelector from "./ImageSelector";

function ImageArrayField({ label, value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const setAt = (idx, v) => {
    const next = list.slice();
    if (v) next[idx] = v; else next.splice(idx, 1);
    onChange(next.filter(Boolean));
  };
  const add = () => onChange(list.concat([""]));
  return (
    <div>
      <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-2 uppercase tracking-wider">
        {label}
        {list.length > 0 && <span className="ml-2 text-[#3A506B]">· {list.length} {list.length === 1 ? "imagen" : "imágenes"}</span>}
      </span>
      <div className="space-y-3">
        {list.map((url, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <div className="flex-1">
              <ImageSelector label={`Imagen ${idx + 1}`} value={url} onChange={(v) => setAt(idx, v)} />
            </div>
            <button
              type="button"
              onClick={() => setAt(idx, "")}
              className="mt-7 text-red-600/70 hover:text-red-700 p-1"
              title="Eliminar"
            >
              <XIcon size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="w-full border border-dashed border-[#16335A]/20 hover:border-[#16335A]/50 hover:bg-[#FDFBF7] text-[#16335A] py-2 inline-flex items-center justify-center gap-2 text-xs font-medium transition-colors"
          style={{ borderRadius: "2px" }}
        >
          <Plus size={12} /> Añadir imagen al carrusel
        </button>
      </div>
    </div>
  );
}

// Field schemas per block type. Items-based blocks have an `items` array
// whose entries follow `itemFields`. Single-form blocks use `fields`.
// "amount" + "pricing_mode" together drive the auto-calculated booking summary.
const PRICING_MODE_OPTIONS = [
  ["included", "Incluido en el total"],
  ["excluded", "Excluido del total"],
  ["on_request", "Precio bajo petición"],
  ["unavailable", "No disponible"],
];

const PRICING_ITEM_FIELDS = [
  { key: "amount", label: "Importe (€)", type: "number", placeholder: "0.00" },
  { key: "pricing_mode", label: "Cálculo del total", type: "select", options: PRICING_MODE_OPTIONS },
];

// Field-group helpers: prefixes a list of fields with `prefix.` so we can store
// outbound_* / return_* on the same item without nested object plumbing.
const FLIGHT_LEG_FIELDS = (prefix, legLabel) => [
  { key: `${prefix}_airline`, label: `${legLabel} · Aerolínea`, type: "text" },
  { key: `${prefix}_number`, label: `${legLabel} · Nº vuelo`, type: "text" },
  { key: `${prefix}_route`, label: `${legLabel} · Ruta`, type: "text", placeholder: "MAD → FCO" },
  { key: `${prefix}_date`, label: `${legLabel} · Fecha`, type: "text" },
  { key: `${prefix}_departure`, label: `${legLabel} · Hora salida`, type: "text" },
  { key: `${prefix}_arrival`, label: `${legLabel} · Hora llegada`, type: "text" },
  { key: `${prefix}_duration`, label: `${legLabel} · Duración`, type: "text" },
  { key: `${prefix}_stops`, label: `${legLabel} · Escalas`, type: "text" },
];

export const BLOCK_SCHEMAS = {
  client: {
    fields: [
      { key: "email", label: "Email", type: "email" },
      { key: "phone", label: "Teléfono", type: "text" },
      { key: "travelers", label: "Nº viajeros", type: "text", placeholder: "Ej: 2 adultos + 1 niño (8a)" },
      { key: "budget", label: "Presupuesto aproximado", type: "text" },
      { key: "trip_type", label: "Tipo de viaje", type: "text", placeholder: "Negocio / Ocio / Mixto" },
      { key: "notes", label: "Observaciones", type: "textarea" },
    ],
  },
  trip: {
    fields: [
      { key: "destination", label: "Destino", type: "text" },
      { key: "departure_airport", label: "Aeropuerto de salida", type: "text" },
      { key: "arrival_airport", label: "Aeropuerto de llegada", type: "text" },
      { key: "duration", label: "Duración", type: "text", placeholder: "Ej: 5 días / 4 noches" },
      { key: "hotel_board", label: "Régimen del hotel", type: "text", placeholder: "AD / MP / PC / TI" },
      { key: "insurance_included", label: "Seguro incluido", type: "select", options: ["", "Sí", "No"] },
      { key: "description", label: "Descripción", type: "textarea" },
    ],
  },
  flights: {
    itemLabel: "Opción de vuelo (ida + vuelta)",
    defaultSelectionMode: "single",
    itemFields: [
      { key: "name", label: "Nombre de la opción", type: "text", placeholder: "Ej: Opción 1 · Vuelo directo Iberia" },
      ...FLIGHT_LEG_FIELDS("outbound", "Ida"),
      ...FLIGHT_LEG_FIELDS("return", "Vuelta"),
      { key: "baggage", label: "Equipaje incluido", type: "text" },
      { key: "price", label: "Precio total ida + vuelta (texto)", type: "text" },
      ...PRICING_ITEM_FIELDS,
      { key: "affiliate_url", label: "Enlace de afiliado", type: "url" },
      { key: "notes", label: "Observaciones", type: "textarea" },
    ],
  },
  hotels: {
    itemLabel: "Hotel",
    defaultSelectionMode: "single",
    itemFields: [
      { key: "name", label: "Nombre", type: "text" },
      { key: "category", label: "Categoría", type: "text", placeholder: "4★ / Boutique / Resort" },
      { key: "address", label: "Dirección", type: "text" },
      { key: "location", label: "Ubicación / zona", type: "text" },
      { key: "rating", label: "Valoración", type: "text", placeholder: "8.7 / Booking" },
      { key: "room_type", label: "Tipo de habitación", type: "text" },
      { key: "board", label: "Régimen", type: "text" },
      { key: "nights", label: "Nº noches", type: "text" },
      { key: "price_per_night", label: "Precio por noche (texto)", type: "text" },
      { key: "services", label: "Servicios", type: "textarea", placeholder: "Spa, parking, desayuno..." },
      { key: "description", label: "Descripción", type: "textarea" },
      { key: "images", label: "Galería de imágenes", type: "image_array" },
      { key: "image_url", label: "Imagen principal (fallback)", type: "image" },
      { key: "map_url", label: "Mapa (URL Google Maps)", type: "url" },
      { key: "price", label: "Precio total (texto)", type: "text" },
      ...PRICING_ITEM_FIELDS,
      { key: "affiliate_url", label: "Enlace de afiliado", type: "url" },
    ],
  },
  transfers: {
    itemLabel: "Opción de traslado",
    defaultSelectionMode: "single",
    itemFields: [
      { key: "type", label: "Tipo", type: "text", placeholder: "Transfer privado / Taxi / Coche / Público / Sin traslado" },
      { key: "company", label: "Empresa", type: "text" },
      { key: "schedule", label: "Horario / disponibilidad", type: "text" },
      { key: "duration", label: "Tiempo aproximado", type: "text" },
      { key: "description", label: "Descripción", type: "textarea" },
      { key: "included", label: "Incluido", type: "select", options: ["", "Sí", "No"] },
      { key: "image_url", label: "Imagen", type: "image" },
      { key: "price", label: "Precio (texto)", type: "text" },
      ...PRICING_ITEM_FIELDS,
      { key: "affiliate_url", label: "Enlace de afiliado", type: "url" },
    ],
  },
  restaurants: {
    itemLabel: "Restaurante",
    defaultSelectionMode: "multi",
    itemFields: [
      { key: "name", label: "Nombre", type: "text" },
      { key: "cuisine", label: "Tipo de cocina", type: "text" },
      { key: "price_avg", label: "Precio medio (texto)", type: "text" },
      { key: "address", label: "Ubicación", type: "text" },
      { key: "schedule", label: "Horario", type: "text" },
      { key: "description", label: "Descripción", type: "textarea" },
      { key: "images", label: "Galería de imágenes", type: "image_array" },
      { key: "image_url", label: "Imagen principal (fallback)", type: "image" },
      { key: "map_url", label: "Mapa (URL)", type: "url" },
      ...PRICING_ITEM_FIELDS,
      { key: "affiliate_url", label: "Enlace para reservar", type: "url" },
    ],
  },
  activities: {
    itemLabel: "Actividad",
    defaultSelectionMode: "multi",
    itemFields: [
      { key: "name", label: "Nombre", type: "text" },
      { key: "duration", label: "Duración", type: "text" },
      { key: "schedule", label: "Horario", type: "text" },
      { key: "includes", label: "Qué incluye", type: "textarea" },
      { key: "price", label: "Precio (texto)", type: "text" },
      ...PRICING_ITEM_FIELDS,
      { key: "description", label: "Descripción", type: "textarea" },
      { key: "images", label: "Galería de imágenes", type: "image_array" },
      { key: "image_url", label: "Imagen principal (fallback)", type: "image" },
      { key: "affiliate_url", label: "Enlace de afiliado", type: "url" },
    ],
  },
  excursions: {
    itemLabel: "Excursión",
    defaultSelectionMode: "multi",
    itemFields: [
      { key: "name", label: "Nombre", type: "text" },
      { key: "company", label: "Empresa", type: "text" },
      { key: "duration", label: "Duración", type: "text" },
      { key: "includes", label: "Qué incluye", type: "textarea" },
      { key: "price", label: "Precio (texto)", type: "text" },
      ...PRICING_ITEM_FIELDS,
      { key: "description", label: "Descripción", type: "textarea" },
      { key: "images", label: "Galería de imágenes", type: "image_array" },
      { key: "image_url", label: "Imagen principal (fallback)", type: "image" },
      { key: "affiliate_url", label: "Enlace de afiliado", type: "url" },
    ],
  },
  transport: {
    itemLabel: "Transporte",
    itemFields: [
      { key: "category", label: "Categoría", type: "select", options: ["", "Coche de alquiler", "Tren", "Ferry", "Transporte público", "Taxi", "Otros"] },
      { key: "company", label: "Empresa", type: "text" },
      { key: "price", label: "Precio (texto)", type: "text" },
      ...PRICING_ITEM_FIELDS,
      { key: "affiliate_url", label: "Enlace de afiliado", type: "url" },
      { key: "notes", label: "Observaciones", type: "textarea" },
    ],
  },
  insurance: {
    fields: [
      { key: "company", label: "Compañía", type: "text" },
      { key: "price", label: "Precio (texto)", type: "text" },
      ...PRICING_ITEM_FIELDS,
      { key: "coverage", label: "Coberturas", type: "textarea" },
      { key: "affiliate_url", label: "Enlace de afiliado", type: "url" },
    ],
  },
  extras: {
    fields: [
      { key: "tips", label: "Consejos del destino", type: "textarea" },
      { key: "what_to_bring", label: "Qué llevar", type: "textarea" },
      { key: "weather", label: "Clima", type: "text" },
      { key: "documents", label: "Documentación", type: "text" },
      { key: "currency", label: "Moneda", type: "text" },
      { key: "plugs", label: "Enchufes", type: "text" },
      { key: "internet", label: "Internet / SIM", type: "text" },
      { key: "language", label: "Idioma", type: "text" },
      { key: "personal_recommendations", label: "Recomendaciones personales", type: "textarea" },
    ],
  },
  private_notes: {
    fields: [
      { key: "notes", label: "Notas privadas (solo visibles para ti)", type: "textarea" },
    ],
  },
};

function Field({ field, value, onChange }) {
  const common = {
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    className: "w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm",
    style: { borderRadius: "2px" },
    placeholder: field.placeholder,
  };
  return (
    <label className="block">
      <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">
        {field.label}
      </span>
      {field.type === "textarea" ? (
        <textarea {...common} rows={3} />
      ) : field.type === "select" ? (
        <select {...common}>
          {field.options.map((opt) => {
            const [v, l] = Array.isArray(opt) ? opt : [opt, opt || "—"];
            return (<option key={v} value={v}>{l || "—"}</option>);
          })}
        </select>
      ) : field.type === "number" ? (
        <input
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          {...common}
          data-testid={field.testId || undefined}
        />
      ) : (
        <input type={field.type === "url" ? "url" : field.type} {...common} />
      )}
    </label>
  );
}

/**
 * AffiliateUrlField — smart affiliate paste.
 * When user finishes editing the URL, asks the backend to detect provider.
 * - add → silently apply with toast
 * - kept → ok
 * - conflict → modal to choose
 * - unknown → leave as-is
 */
function AffiliateUrlField({ value, onChange }) {
  const [conflict, setConflict] = useState(null);
  const [note, setNote] = useState(null); // {type, text}

  const handleBlur = async () => {
    const url = (value || "").trim();
    if (!url || !url.startsWith("http")) return;
    try {
      const { data } = await api.post("/admin/affiliates/check", { url });
      if (data.action === "add") {
        onChange(data.new_url);
        setNote({ type: "ok", text: `Tu ID de ${data.provider} se ha añadido automáticamente.` });
        setTimeout(() => setNote(null), 3500);
      } else if (data.action === "kept") {
        setNote({ type: "ok", text: `Ya lleva tu ID de ${data.provider}.` });
        setTimeout(() => setNote(null), 2500);
      } else if (data.action === "conflict") {
        setConflict(data);
      } else if (data.action === "unknown" && data.provider) {
        setNote({ type: "warn", text: `Provider configurado incompletamente (slug: ${data.provider}).` });
        setTimeout(() => setNote(null), 3500);
      }
    } catch { /* ignore */ }
  };

  const applyReplace = async () => {
    onChange(conflict.suggested_url);
    try {
      await api.post("/admin/affiliates/log-replacement", {
        provider: conflict.provider,
        from_value: conflict.current_value,
        to_value: conflict.our_value,
        original_url: conflict.original_url,
        final_url: conflict.suggested_url,
      });
    } catch { /* ignore */ }
    setNote({ type: "ok", text: `ID reemplazado por el tuyo (${conflict.our_value}).` });
    setTimeout(() => setNote(null), 3500);
    setConflict(null);
  };

  return (
    <>
      <label className="block">
        <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider inline-flex items-center gap-2">
          Enlace de afiliado <Wand2 size={11} className="text-[#E8C97A]" />
        </span>
        <input
          type="url"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="Pega la URL y al salir aplicamos tu ID automáticamente"
          className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
          style={{ borderRadius: "2px" }}
        />
        {note && (
          <p className={`mt-1 text-xs ${note.type === "ok" ? "text-green-700" : "text-orange-700"}`}>
            ✦ {note.text}
          </p>
        )}
        {value && (
          <a href={value} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[#16335A] hover:underline">
            Probar enlace <ExternalLink size={11} />
          </a>
        )}
      </label>

      {conflict && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setConflict(null)}>
          <div className="bg-white max-w-lg w-full p-7 space-y-4" style={{ borderRadius: "2px" }} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl text-[#0A192F]">Conflicto de afiliación</h3>
            <p className="text-sm text-[#3A506B]">
              Esta URL ya tiene un ID de afiliado de <strong>{conflict.provider_name}</strong> distinto al tuyo.
            </p>
            <div className="bg-[#FDFBF7] border border-[#16335A]/10 p-4 text-sm space-y-1.5" style={{ borderRadius: "2px" }}>
              <div><span className="text-[#16335A]/60 uppercase tracking-wider text-[0.65rem]">Parámetro: </span><code className="text-[#16335A]">{conflict.param_name}</code></div>
              <div><span className="text-[#16335A]/60 uppercase tracking-wider text-[0.65rem]">ID actual: </span><code className="text-red-700">{conflict.current_value}</code></div>
              <div><span className="text-[#16335A]/60 uppercase tracking-wider text-[0.65rem]">Tu ID: </span><code className="text-green-700">{conflict.our_value}</code></div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setConflict(null)} className="px-5 py-2.5 text-sm text-[#3A506B] hover:text-[#16335A]">
                Mantener URL original
              </button>
              <button onClick={applyReplace} className="bg-[#16335A] hover:bg-[#214A82] text-white px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2">
                Reemplazar por mi ID
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ItemEditor({ item, fields, onChange, onRemove, index, proposalType = "informative", canBeChosen = false }) {
  const isBooking = proposalType === "booking";
  const chosen = item.is_chosen === true;
  const missingAffiliate = isBooking && chosen && !item.affiliate_url;
  return (
    <div
      className={`bg-[#FDFBF7] border p-5 relative ${chosen && isBooking ? "border-[#E8C97A] ring-1 ring-[#E8C97A]/30" : "border-[#16335A]/10"}`}
      style={{ borderRadius: "2px" }}
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="text-xs font-semibold text-[#16335A] uppercase tracking-wider">#{index + 1}</span>
        <div className="flex items-center gap-3">
          {canBeChosen && isBooking && (
            <button
              type="button"
              onClick={() => onChange({ ...item, is_chosen: !chosen })}
              data-testid={`toggle-chosen-${index}`}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border transition-colors ${
                chosen
                  ? "bg-[#E8C97A] border-[#E8C97A] text-[#0A192F] font-semibold"
                  : "border-[#16335A]/20 text-[#3A506B] hover:border-[#E8C97A] hover:text-[#16335A]"
              }`}
              style={{ borderRadius: "9999px" }}
              title="Marca esta opción como la elegida para la propuesta de reserva"
            >
              <Wand2 size={12} /> {chosen ? "Elegido" : "Marcar como elegido"}
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="text-red-600/70 hover:text-red-700 inline-flex items-center gap-1 text-xs"
          >
            <Trash2 size={14} /> Eliminar
          </button>
        </div>
      </div>
      {missingAffiliate && (
        <p className="mb-3 bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-xs">
          ⚠ Falta el enlace de afiliado en la opción elegida — el cliente no tendrá botón «Reservar ahora».
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" || f.type === "image" || f.type === "image_array" || f.key === "affiliate_url" ? "md:col-span-2" : ""}>
            {f.key === "affiliate_url" ? (
              <AffiliateUrlField
                value={item[f.key]}
                onChange={(v) => onChange({ ...item, [f.key]: v })}
              />
            ) : f.type === "image" ? (
              <ImageSelector
                label={f.label}
                value={item[f.key]}
                onChange={(v) => onChange({ ...item, [f.key]: v })}
              />
            ) : f.type === "image_array" ? (
              <ImageArrayField
                label={f.label}
                value={item[f.key]}
                onChange={(v) => onChange({ ...item, [f.key]: v })}
              />
            ) : (
              <Field
                field={f}
                value={item[f.key]}
                onChange={(v) => onChange({ ...item, [f.key]: v })}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BlockEditor({ block, onChange, proposalType = "informative" }) {
  const [open, setOpen] = useState(["client", "trip"].includes(block.type));
  const schema = BLOCK_SCHEMAS[block.type];
  if (!schema) return null;

  const items = block.data?.items || [];
  const updateField = (key, value) => onChange({ ...block, data: { ...block.data, [key]: value } });
  const updateItem = (idx, newItem) => {
    const next = items.slice();
    next[idx] = newItem;
    onChange({ ...block, data: { ...block.data, items: next } });
  };
  const addItem = () => {
    const next = items.concat([{ id: `it_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}` }]);
    onChange({ ...block, data: { ...block.data, items: next } });
  };
  const removeItem = (idx) => {
    onChange({ ...block, data: { ...block.data, items: items.filter((_, i) => i !== idx) } });
  };
  const toggleHidden = (e) => {
    e.stopPropagation();
    onChange({ ...block, hidden: !block.hidden });
  };

  return (
    <section
      data-testid={`block-${block.type}`}
      className={`bg-white border border-[#16335A]/15 transition-all ${block.hidden ? "opacity-60" : ""}`}
      style={{ borderRadius: "2px" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 hover:bg-[#FDFBF7] transition-colors"
      >
        <div className="flex items-center gap-4">
          <h3 className="font-display text-lg lg:text-xl text-[#0A192F] text-left">
            {block.title}
            {block.type === "private_notes" && <span className="ml-2 text-xs italic text-[#3A506B]">(no visible al cliente)</span>}
          </h3>
          {schema.itemFields && items.length > 0 && (
            <span className="text-xs text-[#16335A]/60">{items.length} {items.length === 1 ? "elemento" : "elementos"}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span onClick={toggleHidden} className="text-xs text-[#3A506B] hover:text-[#16335A] underline" data-testid={`toggle-hide-${block.type}`}>
            {block.hidden ? "Mostrar" : "Ocultar"}
          </span>
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#16335A]/10 p-5 space-y-5">
          {schema.itemFields && schema.defaultSelectionMode && (
            <div className="bg-[#FDFBF7] border border-[#E8C97A]/30 p-4" style={{ borderRadius: "2px" }}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[0.7rem] font-medium text-[#16335A] uppercase tracking-wider">Modo del bloque en la propuesta:</span>
                <select
                  value={block.data?.selection_mode ?? schema.defaultSelectionMode}
                  onChange={(e) => updateField("selection_mode", e.target.value)}
                  data-testid={`${block.type}-selection-mode`}
                  className="px-3 py-1.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
                  style={{ borderRadius: "2px" }}
                >
                  <option value="single">Cliente elige UNA opción (selección única)</option>
                  <option value="multi">Cliente elige VARIAS (favoritos / multi-selección)</option>
                  <option value="informational">Solo informativo (no seleccionable)</option>
                </select>
              </div>
              <p className="text-[0.65rem] text-[#3A506B] mt-2 italic">
                Por defecto este bloque es {schema.defaultSelectionMode === 'single' ? 'selección única' : 'multi-selección'} en la vista del cliente.
              </p>
            </div>
          )}

          {schema.fields && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {schema.fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" || f.type === "image" || f.type === "image_array" || f.key === "affiliate_url" ? "md:col-span-2" : ""}>
                  {f.key === "affiliate_url" ? (
                    <AffiliateUrlField value={block.data?.[f.key]} onChange={(v) => updateField(f.key, v)} />
                  ) : f.type === "image" ? (
                    <ImageSelector label={f.label} value={block.data?.[f.key]} onChange={(v) => updateField(f.key, v)} />
                  ) : f.type === "image_array" ? (
                    <ImageArrayField label={f.label} value={block.data?.[f.key]} onChange={(v) => updateField(f.key, v)} />
                  ) : (
                    <Field field={f} value={block.data?.[f.key]} onChange={(v) => updateField(f.key, v)} />
                  )}
                </div>
              ))}
            </div>
          )}

          {schema.itemFields && (
            <div className="space-y-4">
              {items.map((it, idx) => (
                <ItemEditor
                  key={idx}
                  item={it || {}}
                  fields={schema.itemFields}
                  onChange={(v) => updateItem(idx, v)}
                  onRemove={() => removeItem(idx)}
                  index={idx}
                  proposalType={proposalType}
                  canBeChosen={!!schema.defaultSelectionMode}
                />
              ))}
              <button
                type="button"
                onClick={addItem}
                data-testid={`add-${block.type}`}
                className="w-full border-2 border-dashed border-[#16335A]/20 hover:border-[#16335A]/50 hover:bg-[#FDFBF7] text-[#16335A] py-4 inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                style={{ borderRadius: "2px" }}
              >
                <Plus size={16} /> Añadir {schema.itemLabel.toLowerCase()}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
